import HelperText from '@mui/material/FormHelperText';
import enNationalities from 'i18n-nationality/langs/en.json';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../../components/ui/select';
import {
  InlineOtpVerification,
  InlineOtpVerificationHandle
} from './InlineOtpVerification';
import { PhoneNumberInput } from './PhoneNumberInput';

interface AccountDetailsFormProps {
  setPhoneNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  phoneNumberError: () => boolean;
  setCountryPhoneCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  setNationality: React.Dispatch<React.SetStateAction<string>>;
  nationality?: string;
  setName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setEmail: React.Dispatch<React.SetStateAction<string | undefined>>;
  name?: string;
  email?: string;
  nameError: string | null;
  emailError: string | null;
  nameTouched: boolean;
  emailTouched: boolean;
  setNameTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setEmailTouched: React.Dispatch<React.SetStateAction<boolean>>;
  /** Personal security phrase, required */
  securityPhrase: string;
  setSecurityPhrase: React.Dispatch<React.SetStateAction<string>>;
  securityPhraseError: string | null;
  securityPhraseTouched: boolean;
  setSecurityPhraseTouched: React.Dispatch<React.SetStateAction<boolean>>;
  initialPhoneNumber?: string;
  initialCountryCode?: string;
  /** Current phone number value (for OTP widget) */
  phoneNumber?: string;
  /** Current country code (digits only, no '+') (for OTP widget) */
  countryCode?: string;
  /** Called with the signed verification token after phone OTP succeeds */
  onPhoneVerified: (token: string) => void;
  /** Called with the signed verification token after email OTP succeeds */
  onEmailVerified: (token: string) => void;
  /** Called when phone verification is reset due to value change */
  onPhoneReset: () => void;
  /** Called when email verification is reset due to value change */
  onEmailReset: () => void;
  /** Pre-verified phone token — restores verified state on remount (checkout prefill or mid-session navigation) */
  preVerifiedPhoneToken?: string;
  /** Pre-verified email token — restores verified state on remount after navigating away */
  preVerifiedEmailToken?: string;
  /** Server-side error shown under the phone field */
  phoneServerError?: string | null;
  /** Server-side error shown under the email field */
  emailServerError?: string | null;
  /** Server-side error shown under the security phrase field */
  phraseServerError?: string | null;
  /** Rendered between the nationality and security phrase fields */
  beforePhrase?: React.ReactNode;
}

export interface AccountDetailsFormHandle {
  resetPhoneOtp: () => void;
  resetEmailOtp: () => void;
}

export const AccountDetailsForm = forwardRef<
  AccountDetailsFormHandle,
  AccountDetailsFormProps
>(function AccountDetailsForm(
  {
    setPhoneNumber,
    phoneNumberError,
    setCountryPhoneCode,
    setNationality,
    nationality = 'MY',
    setName,
    setEmail,
    name,
    email,
    nameError,
    emailError,
    nameTouched,
    emailTouched,
    setNameTouched,
    setEmailTouched,
    securityPhrase,
    setSecurityPhrase,
    securityPhraseError,
    securityPhraseTouched,
    setSecurityPhraseTouched,
    initialPhoneNumber,
    initialCountryCode,
    phoneNumber,
    countryCode,
    onPhoneVerified,
    onEmailVerified,
    onPhoneReset,
    onEmailReset,
    preVerifiedPhoneToken,
    preVerifiedEmailToken,
    phoneServerError,
    emailServerError,
    phraseServerError,
    beforePhrase
  },
  ref
) {
  // Initialise from pre-verified tokens so state survives navigation away and back
  const [phoneVerified, setPhoneVerified] = useState(!!preVerifiedPhoneToken);
  const [emailVerified, setEmailVerified] = useState(!!preVerifiedEmailToken);
  const phoneOtpRef = useRef<InlineOtpVerificationHandle>(null);
  const emailOtpRef = useRef<InlineOtpVerificationHandle>(null);
  useImperativeHandle(ref, () => ({
    resetPhoneOtp: () => phoneOtpRef.current?.reset(),
    resetEmailOtp: () => emailOtpRef.current?.reset()
  }));
  const phraseError =
    (securityPhraseTouched && securityPhraseError) || phraseServerError || null;
  const showSecurityPhraseError = !!phraseError;

  const handlePhoneVerified = (token: string) => {
    setPhoneVerified(true);
    onPhoneVerified(token);
  };

  const handleEmailVerified = (token: string) => {
    setEmailVerified(true);
    onEmailVerified(token);
  };

  const handlePhoneReset = () => {
    setPhoneVerified(false);
    onPhoneReset();
  };

  const handleEmailReset = () => {
    setEmailVerified(false);
    onEmailReset();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (!nameTouched) setNameTouched(true);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (!emailTouched) setEmailTouched(true);
  };

  return (
    <div className="w-full max-w-md">
      <div className="mt-6">
        <label
          htmlFor="phone-number-input"
          className="block text-sm font-medium text-gray-700"
        >
          Phone number <span className="text-red-500">*</span>
        </label>
        <PhoneNumberInput
          setCountryPhoneCode={setCountryPhoneCode}
          setPhoneNumber={setPhoneNumber}
          initialPhoneNumber={initialPhoneNumber}
          initialCountryCode={initialCountryCode}
          disabled={phoneVerified}
          onBlur={() => phoneOtpRef.current?.sendIfReady()}
          id="phone-number-input"
        />
        {phoneNumberError() && (
          <HelperText error>Invalid Phone Number</HelperText>
        )}
        {phoneServerError && <HelperText error>{phoneServerError}</HelperText>}
        <InlineOtpVerification
          ref={phoneOtpRef}
          type="phone"
          phoneNumber={phoneNumber}
          countryCode={countryCode}
          onVerified={handlePhoneVerified}
          onReset={handlePhoneReset}
          preVerifiedToken={preVerifiedPhoneToken}
        />
      </div>

      <div className="mt-6">
        <label
          htmlFor="user-name-input"
          className="block text-sm font-medium text-gray-700"
        >
          Name <span className="text-red-500">*</span>
        </label>
        <input
          id="user-name-input"
          type="text"
          name="user_name"
          value={name || ''}
          onChange={handleNameChange}
          placeholder="Your full name"
          className={`mt-1 block w-full rounded-md border ${
            nameTouched && nameError ? 'border-red-500' : 'border-gray-300'
          } px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm`}
        />
        {nameTouched && nameError && (
          <p className="mt-1 text-xs text-red-500">{nameError}</p>
        )}
      </div>

      <div className="mt-6">
        <label
          htmlFor="user-email-input"
          className="block text-sm font-medium text-gray-700"
        >
          Email address <span className="text-red-500">*</span>
        </label>
        <input
          id="user-email-input"
          type="email"
          name="user_email"
          value={email || ''}
          onChange={handleEmailChange}
          disabled={emailVerified}
          placeholder="you@example.com"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="email"
          data-lpignore="true"
          aria-autocomplete="none"
          onBlur={() => emailOtpRef.current?.sendIfReady()}
          className={`mt-1 block w-full rounded-md border ${
            emailTouched && emailError ? 'border-red-500' : 'border-gray-300'
          } px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm`}
        />
        {emailTouched && emailError && (
          <p className="mt-1 text-xs text-red-500">{emailError}</p>
        )}
        {emailServerError && (
          <p className="mt-1 text-xs text-red-500">{emailServerError}</p>
        )}
        <InlineOtpVerification
          ref={emailOtpRef}
          type="email"
          email={email}
          onVerified={handleEmailVerified}
          onReset={handleEmailReset}
          preVerifiedToken={preVerifiedEmailToken}
        />
      </div>

      <div className="mt-6">
        <label
          htmlFor="nationality-select"
          className="block text-sm font-medium text-gray-700"
        >
          Nationality <span className="text-red-500">*</span>
        </label>
        <Select value={nationality} onValueChange={setNationality}>
          <SelectTrigger
            id="nationality-select"
            className="focus:ring-none mt-1 border-gray-300 text-gray-800 focus:ring-0"
          >
            <SelectValue placeholder="Nationality" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(enNationalities.nationalities).map(
              ([code, label]) => (
                <SelectItem key={code} value={code}>
                  {label}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>

      {beforePhrase && <div className="mt-6">{beforePhrase}</div>}

      <div className="mt-6">
        <label
          htmlFor="security-phrase-input"
          className="block text-sm font-medium text-gray-700"
        >
          Security phrase <span className="text-red-500">*</span>
        </label>
        <input
          id="security-phrase-input"
          type="text"
          name="security_phrase"
          value={securityPhrase}
          onChange={(e) => setSecurityPhrase(e.target.value)}
          onBlur={() => setSecurityPhraseTouched(true)}
          placeholder="e.g. pineapple"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-invalid={showSecurityPhraseError}
          aria-describedby={
            showSecurityPhraseError
              ? 'security-phrase-hint security-phrase-error'
              : 'security-phrase-hint'
          }
          className={`mt-1 block w-full rounded-md border ${
            showSecurityPhraseError ? 'border-red-500' : 'border-gray-300'
          } px-3 py-2 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-500 sm:text-sm`}
        />
        <p id="security-phrase-hint" className="mt-1 text-xs text-gray-500">
          Shown with your avatar at checkout. Don't use your password or PIN.
        </p>
        {showSecurityPhraseError && (
          <p id="security-phrase-error" className="mt-1 text-xs text-red-500">
            {phraseError}
          </p>
        )}
      </div>
    </div>
  );
});
